import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import Form from '$/components/ui/Form';
import Checkbox from '$/components/ui/Input/Basics/Checkbox';
import PageBuilder from '$/components/ui/Input/PageBuilder';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { Popup } from '$/components/ui/Popup';

import { usePageEditorContext } from '../PageEditorContext';
import { markdownToHTML } from '$/shared/frontend/utils/MarkdownConverter';
import { renderHTML } from '$/utils/renderHTML';
import { RegFormSettingsForForm } from './types';

interface ConsentCheckboxFieldProps {
  i18n: I18n;
  isInVideoRegistration?: boolean;
  showConditionsPopup?: boolean;
  hideConsentCheckbox?: boolean;
  tabIndex?: number;
}

export const ConsentCheckboxField: React.FC<ConsentCheckboxFieldProps> = ({
  i18n,
  isInVideoRegistration,
  showConditionsPopup,
  hideConsentCheckbox,
  tabIndex,
}) => {
  const popupConsent = React.createRef<HTMLLabelElement>();
  const state = usePageEditorContext();
  const { watch, formState } = useFormContext<RegFormSettingsForForm & { [k: string]: any }>();
  const showConsentCheckbox = isInVideoRegistration
    ? !hideConsentCheckbox
    : watch('showConsentCheckbox');
  const consentCheckboxText =
    watch('consentCheckboxText', formState.defaultValues?.consentCheckboxText) ?? '';

  const { mode } = state;

  if (mode !== PageMode.edit && !showConsentCheckbox) {
    return null;
  }

  return (
    <>
      <div className='reg-field flex flex-row items-start'>
        <Form.Field
          name='showConsentCheckbox'
          className={`w-auto ml-2 mb-0 ${mode !== PageMode.edit ? 'hidden' : ''}`}
          defaultValue={formState.defaultValues?.showConsentCheckbox}
          component={<Checkbox label='' />}
        />

        {mode === PageMode.edit ? (
          <>
            {showConsentCheckbox ? (
              <Form.Field
                name='consentCheckboxText'
                className='ml-2 text-gray-0 text-md mb-0 border w-full -mt-1'
                component={
                  <PageBuilder.TextEdit
                    state={state}
                    placeholder='Enter the consent you require from your registrants'
                  />
                }
              />
            ) : (
              <span className='whitespace-nowrap pl-2 text-md text-color-input-field'>
                Require attendee consent
              </span>
            )}
          </>
        ) : showConsentCheckbox ? (
          <>
            <Form.Field
              name='checkedConsent'
              defaultValue={false}
              className='my-2 ml-2 md:ml-0 hidden-label'
              component={
                <Checkbox
                  labelLeft={state.isRtl}
                  label={
                    <label
                      className='pl-2 text-md text-color-input-field'
                      tabIndex={tabIndex ?? 0}
                      ref={popupConsent}
                    >
                      {renderHTML(markdownToHTML(consentCheckboxText))}
                    </label>
                  }
                />
              }
            />
            <Popup
              context={popupConsent}
              content={<span>{t(i18n)`You must first check this box before continuing.`}</span>}
              eventsEnabled={true}
              open={showConditionsPopup && formState.submitCount > 0}
              position='top left'
              trigger={<div />}
              offset={[-10]}
              style={{
                padding: '5px 15px',
              }}
            />
          </>
        ) : null}
      </div>

      {isInVideoRegistration && showConsentCheckbox && (
        <Form.FieldError name='checkedConsent' className='w-full text-2xl -mt-2' />
      )}
    </>
  );
};
